<template>
  <BaseLayout>
    <BaseDashboard :is-loading="isLoading">
      <div class="h-100 overflow-auto">
        <div class="page-header px-5 py-3">
          <b-form-group
            id="campaign-search-group"
            class="m-0 p-0 col-2"
          >
            <template
              slot="label"
              label-for="campaign-search-group"
              class="form-label"
            >
              Campaign search
            </template>
            <BaseAutocomplete
              id="search-campaign"
              class=""
              data-test="campaign-name-search-input"
              size="sm"
              :items="filteredCampaignOptions"
              v-model="campaignSearch"
              :search-text.sync="campaignSearch"
              @change="onFilterChange"
              @keyup.native="onKeyUp"
            />
          </b-form-group>
          <b-row
            class="ml-3"
            v-if="isManagedCampaigns"
          >
            <b-col cols="12">
              <div class="form-label pb-1">
                Filters
              </div>
            </b-col>

            <b-col>
              <b-form-select
                v-model="stateFilter"
                :options="stateOptions"
                size="sm"
                data-test="state-filter-select"
                @change="onFilterChange"
              />
            </b-col>
            <b-col>
              <b-form-select
                v-model="advertiserFilter"
                :options="advertiserOptions"
                size="sm"
                data-test="advertiser-filter-select"
                @change="onFilterChange"
              />
            </b-col>
            <b-col v-if="isMyDashboard">
              <b-form-select
                v-model="accountManagerFilter"
                :options="accountManagerOptions"
                size="sm"
                data-test="account-manager-filter-select"
                @change="onFilterChange"
              />
            </b-col>
          </b-row>
        </div>
        <div class="table-responsive campaign-table-wrapper">
          <b-table
            ref="campaigns"
            class="card-table table-vcenter text-nowrap"
            :class="{ 'h-100': campaignList.length === 0 }"
            tbody-tr-class="campaign-table-row"
            :striped="false"
            :bordered="false"
            :outlined="false"
            :hover="true"
            :items="getAllCampaigns"
            :fields="getHeaders"
            :busy.sync="isBusy"
            :per-page="perPageSelected"
            :current-page="currentPage"
            :sort-by="sortBy"
            :sort-desc="sortDesc"
            show-empty
            data-test="campaigns-list"
          >
            <template v-slot:table-colgroup="scope">
              <col
                v-for="field in scope.fields"
                :key="field.key"
                :style="{ width: field.width ? field.width : 'auto' }"
              >
            </template>
            <template v-slot:cell(name)="data">
              <span
                data-test="campaign-title"
              >
                <router-link
                  v-if="isDraftState(data.item)"
                  data-test="campaign-details-link"
                  class="large"
                  :to="{
                    name: 'NewCampaign',
                    params: {
                      campaignId: data.item.id,
                      advertiserId: data.item.advertiserId
                    }
                  }"
                >
                  {{ data.value }}
                </router-link>
                <router-link
                  v-else-if="isEndedState(data.item)"
                  class="large"
                  data-test="campaign-view-link"
                  :to="{
                    name: 'ViewCampaign',
                    params: {
                      campaignId: data.item.id,
                      advertiserId: data.item.advertiserId
                    }
                  }"
                >
                  {{ data.value }}
                </router-link>
                <feature
                  name="CAMPAIGN_EDIT"
                  v-else-if="canEdit(data.item)"
                >
                  <router-link
                    class="large campaign-title"
                    data-test="campaign-edit-link"
                    :to="{ name: 'EditCampaign',
                           params: {
                             campaignId: data.item.id,
                             advertiserId: data.item.advertiserId
                           }
                    }"
                  >
                    {{ data.value }}
                  </router-link>
                </feature>
                <template v-else>{{ data.value }}</template>
              </span>

              <div
                class="small text-muted"
                title="Advertiser Name"
                data-test="campaign-advertiser-name"
              >
                {{ getAdvertiserName(data.item.advertiserId) }}
              </div>
            </template>
            <template v-slot:cell(state)="data">
              <span>
                <span
                  v-if="data.item.state"
                  :data-test="`campaign-status-${data.value}`"
                >
                  <span :class="[getStatusIcon(data), getStatusColor(data)]" />
                  {{ getColumnState(data) }}
                </span>

                <a
                  v-if="data.item.state === 'LIVE' && isMyDashboard"
                  href="#"
                  class="icon ml-3"
                  data-test="campaign-status-pause"
                  @click="pauseCampaign(data.item)"
                >
                  <i class="fa-light fa-pause" />
                </a>
                <a
                  v-if="data.item.state === 'PAUSED' && isMyDashboard"
                  href="#"
                  class="icon ml-3"
                  data-test="campaign-status-start"
                  @click="startCampaign(data.item)"
                >
                  <i class="fa-light fa-play" />
                </a>
              </span>
            </template>
            <template v-slot:cell(startDate)="data">
              <small>{{ data.value }}</small>
            </template>
            <template v-slot:cell(endDate)="data">
              <small>{{ data.value }}</small>
            </template>
            <template v-slot:cell(budget)="data">
              <div v-if="data.item.budget">
                <div class="clearfix">
                  <div
                    class="float-left"
                    data-test="budget-spent-percentage"
                  >
                    <strong>{{ getBudgetSpentPercentage(data.item.budgetRemaining) || '0' }}%</strong>
                  </div>
                  <div class="float-right">
                    <small class="text-muted">{{ formattedMoneyAmount(data.item) }}</small>
                  </div>
                </div>
                <div class="progress progress-xs">
                  <div
                    class="progress-bar"
                    :class="setProgressColor(getBudgetSpentPercentage(data.item.budgetRemaining)
                      || 0)"
                    role="progressbar"
                    :style="`width: ${getBudgetSpentPercentage(data.item.budgetRemaining) || '0'}%`"
                    :aria-valuenow="getBudgetSpentPercentage(data.item.budgetRemaining) || '0'"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  />
                </div>
              </div>
            </template>
            <template
              v-slot:cell(accountManager)="data"
            >
              <div
                class="avatar avatar-manager"
                :class="getAvatarClass(data.item.accountManagerUserId)"
              >
                {{ getAvatarInitials(data.item.accountManagerUserId) }}
              </div>
            </template>
            <template v-slot:cell(action)="data">
              <CampaignActions
                :index="data.index"
                :item="data.item"
                @removeCampaign="removeCampaign"
                @startCampaign="startCampaign"
                @setCampaignToDraft="setCampaignToDraft"
                :is-my-dashboard="isMyDashboard"
              />
            </template>
            <template v-slot:table-busy>
              <div class="text-center text-info my-2">
                <div class="loader mx-auto my-5" />
                <strong>Loading...</strong>
              </div>
            </template>
            <template v-slot:empty>
              <div class="row justify-content-md-center py-5">
                <div
                  class="text-center"
                  v-if="isNotSearching"
                >
                  <div>
                    <p
                      class="font-weight-bold intro-message text-center"
                    >
                      Ready to kickstart your contextual journey?<br>Create your first campaign now!
                    </p>
                    <router-link
                      to="/new-campaign"
                      class="btn btn-primary mt-3"
                      data-test="table-new-campaign-button"
                    >
                      New Campaign
                    </router-link>
                  </div>
                </div>
                <div
                  class="col-4 text-center"
                  v-else
                >
                  <h4>Sorry, you don't seem to have any campaigns that match your search.</h4>
                </div>
              </div>
            </template>
          </b-table>
        </div>
        <b-col class="campaign-footer bg-secondary border-top pt-4">
          <BasePagination
            data-test="campaign-pagination"
            :items-count="itemsCount"
            :current-page.sync="currentPage"
            :per-page-selected.sync="perPageSelected"
            items-name="campaigns"
          />
        </b-col>
      </div>
    </BaseDashboard>
  </BaseLayout>
</template>

<script>
import {
  mapActions, mapState, mapMutations, mapGetters,
} from 'vuex';
import * as types from '@/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as accountTypes from '@/modules/account/store/types';

import currenciesMixin from '@/core/mixins/currencies';
import paginationMixin from '@/core/mixins/pagination';

import feature from 'vue-feature-toggle';

import helper from '@/core/helper';
import tableHeaders from '@/views/Campaign/tableHeaders';
import CampaignActions from '@/modules/campaigns/components/CampaignActions';

export default {
  name: 'Dashboard',
  mixins: [
    currenciesMixin,
    paginationMixin,
  ],
  components: {
    CampaignActions,
  },
  data() {
    return {
      ...tableHeaders,
      isBusy: false,
      campaignList: [],
      pageOffset: 1,
      currentPage: 1,
      perPageSelected: 10,
      itemsCount: 0,
      sortBy: '',
      sortDesc: false,
      campaignSearchName: '',
      campaignSearch: '',
      filteredCampaignOptions: [],
      stateFilter: null,
      advertiserFilter: null,
      accountManagerFilter: null,
      stateOptions: [
        { value: null, text: 'State filter' },
        { value: 'LIVE', text: 'Live' },
        { value: 'PAUSED', text: 'Paused' },
        { value: 'DORMANT', text: 'Dormant' },
        { value: 'REVIEW', text: 'Review' },
        { value: 'DRAFT', text: 'Draft' },
        { value: 'ENDED', text: 'Ended' },
      ],
      advertiserOptions: [{ value: null, text: 'Advertiser filter' }],
      accountManagersMap: new Map(),
      accountManagerOptions: [{ value: null, text: 'Account Manager Filter' }],
      isManagedCampaigns: false,
    };
  },
  methods: {
    ...mapActions('account', [
      accountTypes.FETCH_ALL_USERS_IN_ROLE,
      accountTypes.FETCH_ACCOUNT,
    ]),
    ...mapActions('campaigns', [
      campaignTypes.FETCH_EXISTING_ADVERTISERS,
      campaignTypes.FETCH_CURRENCIES,
      campaignTypes.SET_CAMPAIGN_STATE,
      campaignTypes.REMOVE_CAMPAIGN,
      campaignTypes.FETCH_CAMPAIGNS_SEARCH,
      campaignTypes.FETCH_CAMPAIGN_NAME_SEARCH,
      campaignTypes.FETCH_SELF_SERVICE_CAMPAIGN_NAME_SEARCH,
      campaignTypes.FETCH_SELF_SERVICE_CAMPAIGNS,
    ]),
    ...mapMutations('campaigns', [
      campaignTypes.SET_IS_LOADING,
    ]),
    ...mapMutations([
      types.SET_GLOBAL_MESSAGE,
    ]),
    setProgressColor(progress) {
      let colourClass = 'bg-gray';
      if (progress < 100) {
        colourClass = 'bg-success pulse'; /* progress-bar-striped progress-bar-animated */
      }
      return colourClass;
    },
    getStatusColor(status) {
      return new Map([
        ['live', 'bg-success pulse'],
        ['pending', ''],
        ['paused', 'bg-danger'],
        ['dormant', 'bg-primary'],
        ['review', 'bg-warning'],
        ['ended', 'bg-dark'],
      ]).get(this.getColumnState(status).toLocaleLowerCase()) || 'bg-gray';
    },
    canEdit(item) {
      return ['LIVE', 'REVIEW', 'PAUSED', 'DORMANT'].includes(item.state) && feature.isVisible('CAMPAIGN_EDIT');
    },
    isDraftState(item) {
      return item.state === 'DRAFT';
    },
    isEndedState(item) {
      return item.state === 'ENDED';
    },
    formattedMoneyAmount(item) {
      const currencySet = this.currencies.find(
        (currency) => currency.id === item.currencyId,
      );
      if (typeof currencySet === 'undefined') return item.budget || 0;
      const currencySymbol = this.currencyMap[currencySet.code];
      return this.getFormattedMoneyAmount(
        item.budget,
        currencySymbol,
      );
    },
    pauseCampaign(item) {
      this.isBusy = true;
      this.SET_CAMPAIGN_STATE({
        advertiserId: item.advertiserId,
        campaignId: item.id,
        state: 'PAUSED',
      })
        .then((response) => {
          this.campaignList
            .find((campaign) => campaign.id === response.id).state = response.state;
          this.isBusy = false;
        });
    },
    startCampaign(item) {
      this.isBusy = true;
      this.SET_CAMPAIGN_STATE({
        advertiserId: item.advertiserId,
        campaignId: item.id,
        state: 'LIVE',
      })
        .then((response) => {
          this.campaignList
            .find((campaign) => campaign.id === response.id).state = response.state;
          this.isBusy = false;
          this.SET_GLOBAL_MESSAGE({
            message: 'Campaign was set Live.',
            type: 'info',
          });
        });
    },
    setCampaignToDraft(item) {
      this.isBusy = true;

      this.SET_CAMPAIGN_STATE({
        advertiserId: item.advertiserId,
        campaignId: item.id,
        state: 'DRAFT',
        isSelfServiceDashboard: !this.isMyDashboard,
      })
        .then(() => {
          this.campaignList
            .find((i) => i.id === item.id).state = 'DRAFT';
          this.isBusy = false;
          this.SET_GLOBAL_MESSAGE({
            message: 'Campaign was set to DRAFT.',
            type: 'info',
          });
        });
    },
    removeCampaign(item) {
      this.REMOVE_CAMPAIGN({
        advertiserId: item.advertiserId,
        campaignId: item.id,
        campaignName: item.name,
      })
        .finally(() => {
          this.$refs.campaigns.refresh();
        }).catch(() => {
          this.$refs.campaigns.refresh();
        });
    },
    async getAllCampaigns(context) {
      const {
        currentPage, perPage, sortBy, sortDesc,
      } = context;
      const params = {
        page: currentPage, perPage, sortBy, sortDesc,
      };

      const account = await this.FETCH_ACCOUNT();
      if (this.isNewAccount(account)) {
        return [];
      }

      if (this.isMyDashboard && this.GET_IS_MANAGED_ACCOUNT) {
        return this.FETCH_CAMPAIGNS_SEARCH({ params, filters: this.filters })
          .then((campaignList) => Promise.all(campaignList)
            .then((response) => {
              this.campaignList = response[0];
              this.itemsCount = response[1];
              return response[0] || [];
            }));
      }

      return this.FETCH_SELF_SERVICE_CAMPAIGNS({ params, filters: this.filters, managed: this.GET_IS_MANAGED_ACCOUNT })
        .then((campaignList) => Promise.all(campaignList)
          .then((response) => {
            this.campaignList = response[0];
            this.itemsCount = response[1];
            return response[0] || [];
          }));
    },
    isNewAccount(account) {
      return account.firstName === null && account.advertisers.length === 0;
    },
    getBudgetSpentPercentage(budgetRemaining) {
      return 100 - budgetRemaining;
    },
    getAdvertiserName(id) {
      const advertiser = this.existingAdvertisers
        .find((item) => (item.id === id));
      if (advertiser && advertiser.name) {
        return advertiser.name;
      }
      return '';
    },
    resetPage() {
      this.currentPage = 1;
      this.pageOffset = 1;
    },
    onFilterChange() {
      this.resetPage();
      this.$refs.campaigns.refresh();
    },
    onKeyUp: helper.debounce(function getCampaignsFiltered(event) {
      if (this.isMyDashboard) {
        this.FETCH_CAMPAIGN_NAME_SEARCH(event.target.value)
          .then((campaignData) => {
            this.filteredCampaignOptions = campaignData;
          });
      } else {
        this.FETCH_SELF_SERVICE_CAMPAIGN_NAME_SEARCH(event.target.value)
          .then((campaignData) => {
            this.filteredCampaignOptions = campaignData;
          });
      }
    }, 500),
    createAdvertisersList() {
      this.advertiserOptions = Object.freeze([this.advertiserOptions[0]].concat(this.existingAdvertisers
        .map((item) => ({ value: item.id, text: item.name }))));
    },
    async createAccountManagerList() {
      const users = await this.FETCH_ALL_USERS_IN_ROLE('ACCOUNT_MANAGER');
      const accountManagers = users ? users.userResponses : [];

      this.accountManagerOptions = Object.freeze([this.accountManagerOptions[0]].concat(accountManagers
        .map((accountManager) => ({
          text: `${accountManager.firstName} ${accountManager.lastName}`,
          value: accountManager.id,
        }))));
      this.accountManagersMap = new Map(accountManagers
        .map((accountManager) => ({
          id: accountManager.id,
          initials: `${accountManager.firstName[0]?.toUpperCase()}${accountManager.lastName[0]?.toUpperCase()}`,
          avatarColour: accountManager.properties?.avatarColour || process.env.VUE_APP_AVATAR_DEFAULT_COLOUR,
        }))
        .map((accountManager) => [accountManager.id, accountManager]));
    },
    getColumnState(data) {
      const today = new Date();

      if (data.item.startDate) {
        const startDate = new Date(data.item.startDate * 1000);
        if (data.item.state === 'LIVE' && startDate > today) {
          return 'Pending';
        }
      }
      return data.value;
    },
    getStatusIcon(data) {
      return this.getColumnState(data) === 'Pending' ? 'status-icon status-pending' : 'status-icon';
    },
    getAvatarInitials(accountManagerUserId) {
      return this.accountManagersMap.get(accountManagerUserId)?.initials || 'UN';
    },
    getAvatarClass(accountManagerUserId) {
      return `avatar-${this.accountManagersMap.get(accountManagerUserId)?.avatarColour
      || process.env.VUE_APP_AVATAR_DEFAULT_COLOUR}`;
    },
  },
  computed: {
    ...mapState('campaigns', [
      'campaigns',
      'headers',
      'isLoading',
      'currencies',
      'existingAdvertisers',
    ]),
    ...mapState('account', {
      accountAdvertiserId: 'advertiserId',
      features: 'features',
    }),
    ...mapGetters('account', [
      accountTypes.GET_IS_MANAGED_ACCOUNT,
    ]),
    getHeaders() {
      if (this.isMyDashboard) {
        if (this.isManagedCampaigns) {
          return [
            ...this.campaignListHeaders,
            ...this.suppressedList,
            ...this.managedCampaignsList,
            ...this.actionsHeader,
          ];
        }
        return [
          ...this.campaignListHeaders,
          ...this.suppressedList,
          ...this.actionsHeader,
        ];
      }

      const excludedHeaders = ['impressions'];
      const filteredListOfHeaders = this.campaignListHeaders.filter((header) => !excludedHeaders.includes(header.key));

      return [
        ...filteredListOfHeaders,
        ...this.actionsHeader,
      ];
    },
    isNotSearching() {
      return Object.values(this.filters).every((value) => !value);
    },
    filters() {
      return {
        search: this.campaignSearch,
        state: this.stateFilter,
        advertiserId: this.advertiserFilter,
        accountManagerId: this.accountManagerFilter,
      };
    },
    isMyDashboard() {
      return this.$route.meta.type === 'MY_DASHBOARD';
    },
  },
  async mounted() {
    const {
      pageOffset,
      currentPage,
      sortBy,
      sortDesc,
      search,
    } = this.$route.query;
    this.pageOffset = parseInt(pageOffset, 10) || 1;
    this.currentPage = parseInt(currentPage, 10) || 1;
    this.sortBy = sortBy || '';
    this.sortDesc = (sortDesc) ? JSON.parse(sortDesc) : false;
    this.campaignSearch = search || '';
    if (this.currencies.length === 0) this.FETCH_CURRENCIES();

    const params = this.isMyDashboard ? undefined : false;
    this.isManagedCampaigns = (this.features) ? this.features.includes('MANAGED_CAMPAIGNS') : false;
    await this.FETCH_EXISTING_ADVERTISERS(params);
    this.createAdvertisersList();
  },
  watch: {
    currentPage() {
      this.pageOffset = this.calculatePageOffset(this.currentPage, this.perPageSelected);
    },
    perPageSelected(newVal, oldVal) {
      this.pageOffset = this.calculatePageOffset(this.currentPage, oldVal);
    },
    features() {
      this.isManagedCampaigns = this.features.includes('MANAGED_CAMPAIGNS');
      if (this.isManagedCampaigns) this.createAccountManagerList();
    },
    campaignSearch(newVal, oldVal) {
      if (newVal !== oldVal && newVal === '') this.$refs.campaigns.refresh();
    },
  },
};
</script>

<style lang="scss">
.page-header {
  // background: #3499ad;
  margin: 0;
}
.campaign-table-wrapper{
  height: calc(100% - 146px);
}
.campaign-footer{
  position: fixed;
  left: 16.66667%;
  right: 0;
  bottom: 0;
  width: auto;
}
.nav-tabs {
  border: 0;
}
.input-pills {
  input {
    background: #ffffff50;
    color: #ffffff;
    border-radius: 18px;
    border: 1px solid #fff;
    opacity: 1;
    &::-webkit-input-placeholder {
      /* Chrome/Opera/Safari */
      color: #ffffff;
    }
  }
  .input-icon-addon {
    i {
      color: #ffffff;
    }
  }
}
.budget-cell {
  min-width: 100px;
}
.pulse {
  box-shadow: 0 0 0 rgba(94, 186, 0, 0.4);
  animation: pulse 2s infinite;
}
@-webkit-keyframes pulse {
  0% {
    -webkit-box-shadow: 0 0 0 0 rgba(94, 186, 0, 0.4);
  }
  70% {
    -webkit-box-shadow: 0 0 0 10px rgba(94, 186, 0, 0);
  }
  100% {
    -webkit-box-shadow: 0 0 0 0 rgba(94, 186, 0, 0);
  }
}
@keyframes pulse {
  0% {
    -moz-box-shadow: 0 0 0 0 rgba(94, 186, 0, 0.4);
    box-shadow: 0 0 0 0 rgba(94, 186, 0, 0.4);
  }
  70% {
    -moz-box-shadow: 0 0 0 10px rgba(94, 186, 0, 0);
    box-shadow: 0 0 0 10px rgba(94, 186, 0, 0);
  }
  100% {
    -moz-box-shadow: 0 0 0 0 rgba(94, 186, 0, 0);
    box-shadow: 0 0 0 0 rgba(94, 186, 0, 0);
  }
}
</style>
<style lang="scss" scoped>
.intro-message {
  font-size: 1.5rem;
}
</style>
